import { useEffect, useRef, type ReactElement } from 'react';

interface GoogleCredentialResponse {
  credential?: string;
}

interface GoogleAccountsId {
  initialize(options: {
    client_id: string;
    callback: (response: GoogleCredentialResponse) => void;
  }): void;
  renderButton(
    parent: HTMLElement,
    options: {
      type: 'standard';
      theme: 'outline';
      size: 'large';
      shape: 'rectangular';
      text: 'signin_with';
      width: number;
    },
  ): void;
}

declare global {
  interface Window {
    google?: { accounts: { id: GoogleAccountsId } };
  }
}

let googleScript: Promise<void> | null = null;

function loadGoogleIdentityServices(): Promise<void> {
  if (window.google?.accounts.id) return Promise.resolve();
  googleScript ??= new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-google-identity]');
    const script = existing ?? document.createElement('script');

    const loaded = (): void => {
      if (window.google?.accounts.id) resolve();
      else reject(new Error('Google Identity Services did not initialise'));
    };
    script.addEventListener('load', loaded, { once: true });
    script.addEventListener('error', () => reject(new Error('Google sign-in could not load')), {
      once: true,
    });

    if (!existing) {
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.dataset.googleIdentity = 'true';
      document.head.append(script);
    }
  }).catch((error: unknown) => {
    googleScript = null;
    throw error;
  });
  return googleScript;
}

export interface GoogleSignInButtonProps {
  clientId: string;
  disabled?: boolean;
  onCredential: (credential: string) => void;
  onError: () => void;
  label: string;
}

/** Google's own rendered button, as required by the Google Identity Services branding rules. */
export function GoogleSignInButton({
  clientId,
  disabled = false,
  onCredential,
  onError,
  label,
}: GoogleSignInButtonProps): ReactElement {
  const container = useRef<HTMLDivElement>(null);
  const onCredentialRef = useRef(onCredential);
  const onErrorRef = useRef(onError);
  onCredentialRef.current = onCredential;
  onErrorRef.current = onError;

  useEffect(() => {
    let active = true;
    void loadGoogleIdentityServices()
      .then(() => {
        if (!active || !container.current || !window.google) return;
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: (response) => {
            if (response.credential) onCredentialRef.current(response.credential);
            else onErrorRef.current();
          },
        });
        container.current.replaceChildren();
        window.google.accounts.id.renderButton(container.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          shape: 'rectangular',
          text: 'signin_with',
          width: Math.max(240, Math.round(container.current.getBoundingClientRect().width || 352)),
        });
      })
      .catch(() => {
        if (active) onErrorRef.current();
      });
    return () => {
      active = false;
    };
  }, [clientId]);

  return (
    <div
      ref={container}
      aria-label={label}
      className={disabled ? 'pointer-events-none opacity-50' : undefined}
    />
  );
}
